import { Business } from "@mui/icons-material";
import { Box, CircularProgress, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";

import { PageHeader } from "~ui/atoms/PageHeader/PageHeader";
import { RenderStates } from "~ui/atoms/RenderStates/RenderStates";
import { SearchField } from "~ui/atoms/SearchField/SearchField";
import { MyOrganizationCard } from "~ui/molecules/MyOrganizationCard/MyOrganizationCard";
import { TwoColumnGrid } from "~ui/molecules/TwoColumnGrid/TwoColumnGrid";
import { type Organization } from "~ui/organisms/Organizations/types";

export interface MyOrganizationsTemplateProps {
  data?: Organization[];
  publicPagesEnabled?: boolean;
  status: Utils.QueryStatus;
  showHeader?: boolean;
  searchValue: string;
  onSearchChange: (value: string) => void;
}

function OrganizationsList({
  organizations,
  publicPagesEnabled,
}: {
  organizations: Organization[];
  publicPagesEnabled: boolean;
}) {
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("lg"));

  return (
    <TwoColumnGrid singleColumn={isMobileOrTablet}>
      {organizations.map((organization) => (
        <MyOrganizationCard key={organization.id} organization={organization} publicPagesEnabled={publicPagesEnabled} />
      ))}
    </TwoColumnGrid>
  );
}

export const MyOrganizationsTemplate = ({
  data: organizations,
  publicPagesEnabled = false,
  status,
  showHeader = true,
  searchValue,
  onSearchChange,
}: MyOrganizationsTemplateProps) => {
  return (
    <Box sx={{ maxWidth: "100%", overflowX: "hidden", p: { sm: 2, xs: 1 } }}>
      <Stack spacing={2} sx={{ width: "100%" }}>
        {showHeader && (
          <>
            <PageHeader title="My Organizations" icon={<Business data-variant="page-header" />} />
            <SearchField
              onChange={onSearchChange}
              placeholder="Search organizations..."
              value={searchValue}
              sx={{ mb: 1.5 }}
            />
          </>
        )}
        <RenderStates
          data={organizations}
          empty={<Typography>No organizations found</Typography>}
          error={<Typography color="error">Error loading organizations</Typography>}
          pending={<CircularProgress />}
          status={status}
          success={(orgs) => <OrganizationsList organizations={orgs} publicPagesEnabled={publicPagesEnabled} />}
        />
      </Stack>
    </Box>
  );
};
